import { getCurrentInstance, ref, toValue } from 'vue';
import { set } from 'lodash-es';
import { notifyError } from '@/components/FlashMessages';
import { HTTP_STATUS } from '@/utils/networkUtils';
import type { ValidationError } from '@/api/types';

/**
 * Provides logic to extract server validation errors from responses and exposes them in an `errors` object for vuelidate.
 * You can provide a mapping of server param names to your validation names in `serverParamMap` (e.g. "{ course_name: 'form.courseName' }").
 *
 * It will return a ref `vuelidateExternalResults` for vuelidate containing server errors and a function `processServerError`.
 * Call the function in you api `catch` block. It will handle the server error properly or throws it again.
 */
export function useServerValidation(serverParamMap: { [serverParam: string]: string } = {}) {
    const errors = ref<{ [serverParam: string]: string }>({});
    const definedWatchers: string[] = [];

    const vm = getCurrentInstance();

    /**
     * Sets up a watcher to clear server errors when the model changes
     */
    function setupWatcher(modelPath: string) {
        if (!vm?.proxy || definedWatchers.includes(modelPath)) return;

        // whenever the model changes, clear the server errors
        vm.proxy.$watch(modelPath, () => {
            set(errors.value, modelPath, []);
        });
        definedWatchers.push(modelPath);
    }

    /**
     * Processes server validation errors and maps them based on the `serverParamMap` to component data attribute
     * in a format that can be used by vuelidate external results.
     *
     * Any non validation error, will be thrown again.
     * If the validation contains a generic error no linked to any model, a flash message will be shown.
     */
    function processServerError(error: Error | ValidationError) {
        if (!error || typeof error !== 'object' || !('isAxiosError' in error)) throw error;
        if (error.response?.status !== HTTP_STATUS.UNPROCESSABLE_ENTITY) throw error;

        const data = error.response?.data;
        if (data?.errors) {
            // map server errors to our validation results
            Object.entries(data.errors).forEach(([param, errs]) => {
                const model = toValue(serverParamMap)[param] ?? param;

                setupWatcher(model);
                set(errors.value, model, errs);
            });

            return;
        }

        const errMsg = data.error ?? data.message;
        if (!errMsg) throw error;

        notifyError(errMsg);
    }

    return {
        vuelidateExternalResults: errors,
        processServerError,
    };
}
